import { useState, useEffect } from "react";
import {
  Button,
  CloseToastIcon,
  H4,
  IconButton,
  TextInput,
  Tooltip,
  WarningIcon,
} from "@datapay-by-datacom/design-system";
import {
  IoEyeOutline,
  IoEyeOffOutline,
  IoCopyOutline,
  IoCheckmarkOutline,
} from "react-icons/io5";
import { useAuthenticated } from "../../authn";
import { environment } from "../../environment";
import { useMcpKeys } from "../hooks/useMcpKeys";
import { ApiKeyCreated, ApiKeySummary } from "../../generated/core-api";
import s from "./McpKeyList.module.css";

const MCP_SSE_PATH = "/agentic-ai-mcp/sse";

function buildMcpConfigSnippet(url: string, apiKey: string): string {
  const inner = JSON.stringify(
    { url, headers: { Authorization: `Bearer ${apiKey}` } },
    null,
    2
  );
  const indented = inner.replace(/\n/g, "\n  ");
  return `"payroll-assistant": ${indented},`;
}

const EXPIRY_WARNING_DAYS = 7;

function isExpiringSoon(key: ApiKeySummary): boolean {
  if (!key.expires_at) return false;
  const expiryDate = new Date(key.expires_at);
  const warningDate = new Date();
  warningDate.setDate(warningDate.getDate() + EXPIRY_WARNING_DAYS);
  return expiryDate <= warningDate && expiryDate > new Date();
}

function defaultKeyName(userName?: string): string {
  if (!userName) return "";
  return `${userName}'s MCP Key`;
}

export function McpKeyList() {
  const auth = useAuthenticated();
  const { keys, isLoading, createKey, revokeKey } = useMcpKeys();
  const [name, setName] = useState(defaultKeyName(auth.user.name));
  const [email, setEmail] = useState(auth.user.email ?? "");
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [keyVisible, setKeyVisible] = useState(false);
  const [mcpUrl, setMcpUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    environment()
      .then((env) => {
        if (!cancelled) {
          const origin = new URL(env.BASE_URL).origin;
          setMcpUrl(origin + MCP_SSE_PATH);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const activeKeys = keys.filter((k) => !k.revoked_at);

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;

    createKey.mutate(
      { developer_id: name.trim(), developer_email: email.trim() },
      {
        onSuccess: (data) => {
          setCreatedKey(data);
          setKeyVisible(false);
          setCopiedField(null);
          setName(defaultKeyName(auth.user.name));
          setEmail(auth.user.email ?? "");
        },
      }
    );
  };

  const handleRevoke = (keyId: string) => {
    setRevokingId(keyId);
    revokeKey.mutate(keyId, {
      onSettled: () => setRevokingId(null),
    });
  };

  const handleCopy = async (value: string, field: string) => {
    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 3000);
  };

  if (isLoading) {
    return <div>Loading MCP keys...</div>;
  }

  return (
    <div className={s.container}>
      <H4>MCP API Keys</H4>

      <p className={s.description}>
        Manage API keys for MCP (Model Context Protocol) integrations.
      </p>

      {createdKey && (
        <div className={s.createdKeyBanner}>
          <IconButton
            className={s.dismissButton}
            onPress={() => setCreatedKey(null)}
            aria-label="Dismiss"
          >
            <CloseToastIcon />
          </IconButton>
          <p className={s.createdKeyLabel}>
            Key created successfully. Copy it now - it won&apos;t be shown
            again.
          </p>
          <div className={s.fieldGroup}>
            <span className={s.fieldLabel}>API Key</span>
            <div className={s.createdKeyValue}>
              <input
                className={s.keyInput}
                type={keyVisible ? "text" : "password"}
                value={createdKey.api_key}
                readOnly
                aria-label="API key"
              />
              <Tooltip
                text={keyVisible ? "Hide key" : "Show key"}
                placement="top"
                delay={0}
                closeDelay={300}
              >
                <IconButton
                  onPress={() => setKeyVisible((v) => !v)}
                  aria-label={keyVisible ? "Hide key" : "Show key"}
                >
                  {keyVisible ? (
                    <IoEyeOffOutline size={18} />
                  ) : (
                    <IoEyeOutline size={18} />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip
                text={
                  copiedField === "key" ? "Copied!" : "Copy to clipboard"
                }
                placement="top"
                delay={0}
                closeDelay={300}
              >
                <IconButton
                  onPress={() => handleCopy(createdKey.api_key, "key")}
                  aria-label="Copy API key"
                >
                  {copiedField === "key" ? (
                    <IoCheckmarkOutline size={18} />
                  ) : (
                    <IoCopyOutline size={18} />
                  )}
                </IconButton>
              </Tooltip>
            </div>
          </div>
          {mcpUrl && (
            <>
              <div className={s.fieldGroup}>
                <span className={s.fieldLabel}>MCP URL</span>
                <div className={s.createdKeyValue}>
                  <input
                    className={s.keyInput}
                    type="text"
                    value={mcpUrl}
                    readOnly
                    aria-label="MCP URL"
                  />
                  <Tooltip
                    text={
                      copiedField === "url" ? "Copied!" : "Copy to clipboard"
                    }
                    placement="top"
                    delay={0}
                    closeDelay={300}
                  >
                    <IconButton
                      onPress={() => handleCopy(mcpUrl, "url")}
                      aria-label="Copy MCP URL"
                    >
                      {copiedField === "url" ? (
                        <IoCheckmarkOutline size={18} />
                      ) : (
                        <IoCopyOutline size={18} />
                      )}
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
              <div className={s.fieldGroup}>
                <div className={s.configSnippetHeader}>
                  <span className={s.fieldLabel}>MCP Server Config</span>
                  <Tooltip
                    text={
                      copiedField === "config"
                        ? "Copied!"
                        : "Copy to clipboard"
                    }
                    placement="top"
                    delay={0}
                    closeDelay={300}
                  >
                    <IconButton
                      onPress={() =>
                        handleCopy(
                          buildMcpConfigSnippet(mcpUrl, createdKey.api_key),
                          "config"
                        )
                      }
                      aria-label="Copy config snippet"
                    >
                      {copiedField === "config" ? (
                        <IoCheckmarkOutline size={18} />
                      ) : (
                        <IoCopyOutline size={18} />
                      )}
                    </IconButton>
                  </Tooltip>
                </div>
                <pre className={s.configSnippet}>
                  <code>
                    {buildMcpConfigSnippet(mcpUrl, createdKey.api_key)}
                  </code>
                </pre>
              </div>
            </>
          )}
        </div>
      )}

      <form onSubmit={handleCreate} className={s.createForm}>
        <TextInput
          label="Key Name"
          value={name}
          onChange={(value) => setName(value)}
          isRequired
        />
        <TextInput
          label="Email"
          value={email}
          onChange={(value) => setEmail(value)}
          isRequired
        />
        {createKey.error && (
          <div className={s.error}>Failed to create key. Please try again.</div>
        )}
        <div className={s.formActions}>
          <Button
            variant="primary"
            type="submit"
            isDisabled={
              createKey.isPending || !name.trim() || !email.trim()
            }
          >
            {createKey.isPending ? "Creating..." : "Create Key"}
          </Button>
        </div>
      </form>

      {activeKeys.length === 0 ? (
        <p className={s.emptyState}>No active MCP keys.</p>
      ) : (
        <div className={s.keyList}>
          {activeKeys.map((key) => (
            <div key={key.key_id} className={s.keyItem}>
              <div className={s.keyInfo}>
                <div className={s.keyNameRow}>
                  <span className={s.keyName}>{key.developer_id}</span>
                  {isExpiringSoon(key) && (
                    <span
                      className={s.expiryWarning}
                      title="Expiring soon"
                    >
                      <WarningIcon />
                    </span>
                  )}
                </div>
                <span className={s.keyMeta}>
                  {key.developer_email} &middot; Created{" "}
                  {new Date(key.created_at).toLocaleDateString()}
                  {key.expires_at && (
                    <>
                      {" "}
                      &middot; Expires{" "}
                      {new Date(key.expires_at).toLocaleDateString()}
                    </>
                  )}
                </span>
              </div>
              <Button
                variant="tertiary"
                onPress={() => handleRevoke(key.key_id)}
                isDisabled={revokingId === key.key_id}
              >
                {revokingId === key.key_id ? "Revoking..." : "Revoke"}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
